import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Calendar, Download, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import SEO from '@/components/SEO';
import ScrollToTop from '@/components/ScrollToTop';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const BookingSuccess = () => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');
  const [loading, setLoading] = useState(true);
  const [processed, setProcessed] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const processSuccessfulPayment = async () => {
      // Only process once and only if we have both sessionId and bookingId
      if (processed || !sessionId || !bookingId) {
        setLoading(false);
        return;
      }

      try {
        setProcessed(true);

        // Get booking details
        const { data: booking, error: bookingError } = await supabase
          .from('bookings')
          .select('*')
          .eq('booking_id', bookingId)
          .single();

        if (bookingError) {
          console.error('Error fetching booking:', bookingError);
          setLoading(false);
          return;
        }

        // Only process if payment hasn't been confirmed yet
        if (booking.payment_status === 'paid') {
          console.log('Payment already processed for this booking');
          setLoading(false);
          return;
        }

        // Create Zoom meeting and Google Calendar event for Video appointments
        let zoomData = null;
        let calendarEventId = null;

        if (booking.appointment_type === 'Video') {
          try {
            // Create Zoom meeting
            const { data: zoomResponse, error: zoomError } = await supabase.functions.invoke('create-zoom-meeting', {
              body: {
                topic: `Legal Consultation - ${booking.service}`,
                start_time: booking.appointment_start,
                duration: 60,
                timezone: 'America/Chicago',
                agenda: `Legal consultation with ${booking.full_name} regarding ${booking.service}`,
              },
            });

            if (zoomError) {
              console.error('Error creating Zoom meeting:', zoomError);
            } else {
              zoomData = zoomResponse;
              console.log('Zoom meeting created:', zoomData);
            }

            // Create Google Calendar event
            const appointmentEnd = new Date(new Date(booking.appointment_start).getTime() + 60 * 60 * 1000).toISOString();
            
            const { data: calendarResponse, error: calendarError } = await supabase.functions.invoke('create-calendar-event', {
              body: {
                summary: `Legal Consultation - ${booking.full_name}`,
                description: `Service: ${booking.service}\nClient: ${booking.full_name}\nPhone: ${booking.phone}\nEmail: ${booking.email}`,
                start_time: booking.appointment_start,
                end_time: appointmentEnd,
                timezone: 'America/Chicago',
                attendee_email: booking.email,
                attendee_name: booking.full_name,
                zoom_join_url: zoomData?.join_url,
              },
            });

            if (calendarError) {
              console.error('Error creating calendar event:', calendarError);
            } else {
              calendarEventId = calendarResponse?.event_id;
              console.log('Calendar event created:', calendarEventId);
            }
          } catch (integrationError) {
            console.error('Error with Zoom/Calendar integration:', integrationError);
          }
        }

        // Update payment status and add Zoom/Calendar data
        const updateData: any = { payment_status: 'paid' };
        if (zoomData) {
          updateData.zoom_meeting_id = zoomData.meeting_id;
          updateData.zoom_join_url = zoomData.join_url;
          updateData.zoom_password = zoomData.password;
          updateData.zoom_start_url = zoomData.start_url;
        }
        if (calendarEventId) {
          updateData.calendar_event_id = calendarEventId;
        }

        const { error: updateError } = await supabase
          .from('bookings')
          .update(updateData)
          .eq('booking_id', bookingId);

        if (updateError) {
          console.error('Error updating payment status:', updateError);
        }

        // Send confirmation email to client with Zoom/Calendar details
        try {
          const { data: emailData, error: emailError } = await supabase.functions.invoke('send-booking-confirmation', {
            body: {
              bookingId: booking.booking_id,
              email: booking.email,
              fullName: booking.full_name,
              service: booking.service,
              appointmentType: booking.appointment_type,
              appointmentStart: booking.appointment_start,
              paymentMethod: 'Stripe',
              language: booking.language || language,
              zoomJoinUrl: zoomData?.join_url,
              zoomPassword: zoomData?.password,
              calendarEventId: calendarEventId,
            },
          });
          
          if (emailError) {
            console.error('Error sending confirmation email:', emailError);
            toast({
              title: 'Warning',
              description: 'Payment successful but there was a problem sending the confirmation email.',
              variant: 'destructive',
            });
          } else {
            console.log('Confirmation email sent to client:', emailData);
          }
        } catch (emailError) {
          console.error('Error sending confirmation email:', emailError);
        }

        // Send notification to team with Zoom/Calendar details
        try {
          const { data: notificationData, error: notificationError } = await supabase.functions.invoke('notify-team-booking', {
            body: {
              bookingId: booking.booking_id,
              fullName: booking.full_name,
              email: booking.email,
              phone: booking.phone,
              service: booking.service,
              appointmentType: booking.appointment_type,
              appointmentStart: booking.appointment_start,
              paymentMethod: 'Stripe',
              paymentStatus: 'paid',
              situation: booking.situation,
              language: booking.language || language,
              zoomJoinUrl: zoomData?.join_url,
              zoomStartUrl: zoomData?.start_url,
              zoomPassword: zoomData?.password,
              calendarEventId: calendarEventId,
            },
          });
          
          if (notificationError) {
            console.error('Error sending team notification:', notificationError);
          } else {
            console.log('Team notification sent:', notificationData);
          }
        } catch (notificationError) {
          console.error('Error sending team notification:', notificationError);
        }

      } catch (error) {
        console.error('Error processing payment success:', error);
      } finally {
        setLoading(false);
      }
    };

    processSuccessfulPayment();
  }, [sessionId, bookingId, processed, language]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={t('bookingSuccess.title')}
        description={t('bookingSuccess.subtitle')}
      />
      <ScrollToTop />
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        
        <main className="flex-1 py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <Card>
                <CardHeader className="text-center">
                  <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="h-10 w-10 text-green-600" />
                  </div>
                  <CardTitle className="text-3xl">
                    {t('bookingSuccess.title')}
                  </CardTitle>
                  <CardDescription className="text-lg mt-2">
                    {t('bookingSuccess.subtitle')}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  {bookingId && (
                    <div className="bg-muted p-4 rounded-lg">
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        {t('bookingSuccess.bookingNumber')}
                      </p>
                      <p className="text-lg font-mono font-semibold">{bookingId}</p>
                    </div>
                  )}

                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">
                      {t('bookingSuccess.whatsNext')}
                    </h3>
                    
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-sm font-semibold text-primary">1</span>
                        </div>
                        <div>
                          <p className="font-medium">
                            {t('bookingSuccess.step1')}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {t('bookingSuccess.step1Desc')}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-sm font-semibold text-primary">2</span>
                        </div>
                        <div>
                          <p className="font-medium">
                            {t('bookingSuccess.step2')}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {t('bookingSuccess.step2Desc')}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-sm font-semibold text-primary">3</span>
                        </div>
                        <div>
                          <p className="font-medium">
                            {t('bookingSuccess.step3')}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {t('bookingSuccess.step3Desc')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-6 space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="font-semibold text-blue-900 mb-2">
                        {t('bookingSuccess.reminder')}
                      </p>
                      <p className="text-sm text-blue-800">
                        {t('bookingSuccess.reminderText')}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm font-medium">
                        {t('bookingSuccess.officeLocation')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Daniel Albert Law Firm<br />
                        10925 Beechnut St A106<br />
                        Houston, TX 77072
                      </p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm font-medium">
                        {t('bookingSuccess.contact')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        English: <a href="tel:8329303059" className="text-primary hover:underline">(832) 930-3059</a><br />
                        Español: <a href="tel:8323567254" className="text-primary hover:underline">(832) 356-7254</a><br />
                        Email: <a href="mailto:info@danielalbertlaw.com" className="text-primary hover:underline">info@danielalbertlaw.com</a>
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 pt-6">
                    <Button asChild className="flex-1">
                      <Link to="/">
                        <ArrowRight className="h-4 w-4 mr-2" />
                        {t('bookingSuccess.returnHome')}
                      </Link>
                    </Button>
                    <Button variant="outline" className="flex-1" disabled>
                      <Calendar className="h-4 w-4 mr-2" />
                      {t('bookingSuccess.addToCalendar')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default BookingSuccess;
